import { all, call, put, retry, select, takeEvery, takeLatest } from 'redux-saga/effects';
import ACTIONS from '../app.constants';
import { citationActions, siteActions } from '../actions';
import {
    CitationList,
    CitationActionType,
    Citation,
    AddCitationCommentActionType,
    DeleteCitationsDocumentActionType,
    mergeCitationsDocumentsActionType,
    CitationListActionType,
} from '../modules/citation/types';
import { Api, API_CONFIGURATIONS } from '../services/services';
import { getAccessToken } from './authentication.sagas';
import { ApplicationActionType, Comment, CommentList, Form, FormTypeConfiguration } from '../modules/application/types';
import { CitationUpdateActionType } from '../modules/citation/types';
import defaultCitationEditForm from '../../views/CitationDetailTabs/Tabs/CitationData/defaultEditForm.json';
import { doubleDecodeURL } from '../../utils/utils';
import { getCitationSearchFormId } from '../selectors/site.selectors';
import defaultCitationSearchForm from '../../views/Citations/defaultCitationSearchForm.json';

function* getCitationList(action: CitationListActionType) {
    try {
        yield put(citationActions.getCitations.pending);

        const token: string = yield call(getAccessToken, 'citations.read');

        if (action?.data?.skip === 0) {
            let citationCount: number = 0;
            if (action?.payload?.data?.length) {
                citationCount = yield retry(
                    API_CONFIGURATIONS.MAX_RETRY_COUNT,
                    API_CONFIGURATIONS.RETRY_INTERVAL,
                    Api.getCitationCount,
                    token,
                    action.payload.data,
                );
            }

            yield put(citationActions.getCitationListCount.success(citationCount));
        }

        const response: CitationList = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getCitationList,
            token,
            action?.data,
            action?.payload?.data,
        );

        yield put(citationActions.getCitations.success(response, { limit: action.data.limit, skip: action.data.skip }));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(citationActions.getCitations.error(error));
    }
}

function* applyCitationFilter(action: CitationListActionType) {
    yield getCitationList(action);
}

function* getCitationDetail(action: CitationActionType) {
    const id: string = action.payload.id;
    let editForm: Form = defaultCitationEditForm;

    try {
        yield put(citationActions.getCitationDetail.pending);

        const token: string = yield call(getAccessToken, 'citations.read');

        const citation: Citation = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getCitationDetail,
            token,
            id,
        );

        const citationsTypesToken: string = yield call(getAccessToken, 'citationTypes.read');
        const citationType: FormTypeConfiguration = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getCitationTypeById,
            citationsTypesToken,
            citation.type.id,
        );
        if (citationType?.views?.edit?.formId) {
            try {
                const formsToken: string = yield call(getAccessToken, 'forms.read');
                editForm = yield retry(
                    API_CONFIGURATIONS.MAX_RETRY_COUNT,
                    API_CONFIGURATIONS.RETRY_INTERVAL,
                    Api.getForm,
                    formsToken,
                    citationType.views?.edit.formId,
                );
            } catch (error) {
                yield put(
                    siteActions.setError({
                        isError: false,
                        errorCode: 0,
                        message: 'Something went wrong. Please contact IT support.',
                    }),
                );
            }
        }

        yield put(citationActions.getCitationDetail.success(citation, citationType, editForm));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(citationActions.getCitationDetail.error(error));
    }
}

function* getCitationsFileList(action: CitationActionType) {
    const id: string = action.payload.id;
    try {
        yield put(citationActions.getCitationFileList.pending);
        const token: string = yield call(getAccessToken, 'citations.read');
        const response = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getCitationsFileList,
            token,
            id,
            action.paginationData,
        );
        yield put(citationActions.getCitationFileList.success(response, action?.paginationData));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(citationActions.getCitationFileList.error(error));
    }
}

function* deleteCitationsFileDocument(action: DeleteCitationsDocumentActionType) {
    const citationId: string = action.payload.citationId;
    const fileId: string = action.payload.fileId;
    try {
        yield put(citationActions.deleteCitationsFileDocument.pending);
        const token: string = yield call(getAccessToken, 'citations.write');
        yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.deleteCitationsFileDocument,
            token,
            citationId,
            fileId,
        );
        yield put(citationActions.deleteCitationsFileDocument.success(doubleDecodeURL(fileId)));
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: 'File has been removed.' }));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(citationActions.deleteCitationsFileDocument.error(error));
    }
}

function* downloadCitationsFileDocument(action: mergeCitationsDocumentsActionType) {
    const citationId: string = action.payload.citationId;
    const fileId: string = action.payload.fileId;
    const fileName: string = action.payload.fileName;
    try {
        yield put(citationActions.downloadCitationsFileDocument.pending);
        const token: string = yield call(getAccessToken, 'citations.read');
        const fileTokenResult = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getCitationsFileToken,
            token,
            citationId,
            fileId,
        );
        yield put(citationActions.downloadCitationsFileDocument.success);
        yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.downloadFileDocument,
            fileId,
            fileTokenResult.token,
            fileName,
        );
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(citationActions.downloadCitationsFileDocument.error(error));
    }
}

function* updateCitation(action: CitationUpdateActionType) {
    const data: Citation = action.payload.data;
    const citationId: string = action.payload.citationId;
    const isFees: boolean = action.payload.fees;

    try {
        yield put(citationActions.updateCitation.pending);
        const token: string = yield call(getAccessToken, 'citations.write');
        yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.updateCitation,
            token,
            data,
            citationId,
        );

        if (!isFees) {
            yield put(citationActions.getCitationDetailBegin(citationId));
        }

        yield put(citationActions.updateCitation.success(data));
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Information has been saved.' }));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(citationActions.updateCitation.error(error));
    }
}

function* addCitationComment(action: AddCitationCommentActionType) {
    const data: string = action.payload.data;
    const citationId: string = action.payload.citationId;
    try {
        yield put(citationActions.addCitationComment.pending);
        const token: string = yield call(getAccessToken, 'citations.write');
        const result: Comment = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.addCitationComment,
            token,
            data,
            citationId,
        );
        yield put(citationActions.addCitationComment.success(result));
        yield put(
            siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Citation comment has been added.' }),
        );
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(citationActions.addCitationComment.error(error));
    }
}

function* getCitationCommentList(action: ApplicationActionType) {
    const citationId: string = action.payload.id;
    try {
        yield put(citationActions.getCitationCommentList.pending);
        const token: string = yield call(getAccessToken, 'citations.read');
        const response: CommentList = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getCitationCommentList,
            token,
            citationId,
            action.paginationData,
        );
        yield put(citationActions.getCitationCommentList.success(response, action.paginationData));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(citationActions.getCitationCommentList.error(error));
    }
}

function* getCitationSearchForm() {
    try {
        let citationSearchForm: Form = defaultCitationSearchForm;
        yield put(citationActions.getCitationSearchForm.pending);
        const token: string = yield call(getAccessToken, 'forms.read');
        const formId: string = yield select(getCitationSearchFormId);
        if (!!formId) {
            try {
                citationSearchForm = yield retry(
                    API_CONFIGURATIONS.MAX_RETRY_COUNT,
                    API_CONFIGURATIONS.RETRY_INTERVAL,
                    Api.getForm,
                    token,
                    formId,
                );
            } catch (error) {
                yield put(citationActions.getCitationSearchForm.error(error));
            }
        }
        yield put(citationActions.getCitationSearchForm.success(citationSearchForm));
    } catch (error) {
        yield put(siteActions.setError(error));
    }
}

export default function* rootSaga() {
    yield all([
        takeEvery(ACTIONS.GET_CITATION_LIST_BEGIN, getCitationList),
        takeEvery(ACTIONS.GET_CITATION_DETAIL_BEGIN, getCitationDetail),
        takeEvery(ACTIONS.GET_CITATIONS_FILE_LIST_BEGIN, getCitationsFileList),
        takeEvery(ACTIONS.DELETE_CITATIONS_FILE_DOCUMENT_BEGIN, deleteCitationsFileDocument),
        takeEvery(ACTIONS.DOWNLOAD_CITATIONS_FILE_DOCUMENT_BEGIN, downloadCitationsFileDocument),
        takeEvery(ACTIONS.UPDATE_CITATION_BEGIN, updateCitation),

        takeLatest(ACTIONS.APPLY_CITATION_FILTERS, applyCitationFilter),
        takeEvery(ACTIONS.GET_CITATION_SEARCH_FORM_BEGIN, getCitationSearchForm),
        takeLatest(ACTIONS.ADD_CITATION_COMMENT_BEGIN, addCitationComment),
        takeLatest(ACTIONS.CITATION_COMMENT_LIST_BEGIN, getCitationCommentList),
    ]);
}
